import { Effect, Schema, SchemaTransformation } from 'effect';
import { DateTime } from 'luxon';
import { emptySpaceTimeConfig, toSpaceTimeConfig } from '../spacetime/spacetimeConfig.js';
import { toAccessPoints } from './accessPoint.js';
import { getActivityById, toActivities, unspecifiedActivity } from './activity.js';
import { defaultSiteFeatures, toSiteFeatures } from './features.js';
import { hasFlag, KnownFlags } from './flags.js';
import { toGroupDefs } from './group.js';
import { defaultPageSetup, PageEnum, pageFromUrl, PageType, toPages } from './page.js';
import { PolicySchema, toPolicies } from './policy.js';
import { emptySiteInfo, toSiteInfo } from './siteInfo.js';
import { slotDescription, spacesActiveAndSorted, toSpace } from './space.js';
import { cartTicketOverQuota, spaceQuotas, spaceQuotaInfo, quotaInfoText } from './quota.js';
import { emptyConfirmationTextConfig, emptySiteSettings, toSiteSettings } from './siteSettings.js';
import { toPayOutsideSystemRole } from '../capabilities.js';
import { toPriceOptions } from '../discount.js';
import { toPunchCardDef } from '../punchcard.js';
import { toSlotDefs } from './slotDef.js';
const siteDataVersion = 3;
const toSiteLocale = (raw) => {
    if (raw === 'sv' || raw === 'se')
        return 'sv';
    if (raw === 'en')
        return 'en';
    return 'no';
};
const toSiteCategory = (raw) => {
    if (raw === 'padel')
        return 'padel';
    if (raw === 'other')
        return 'other';
    return 'dog';
};
const toEventRecords = (raw) => {
    if (!raw)
        return [];
    const parsed = raw;
    // Remove event records older than one year.
    const oneYearAgo = DateTime.now().minus({ year: 1 });
    return parsed.filter((p) => {
        const from = DateTime.fromISO(p.from);
        const to = DateTime.fromISO(p.to);
        if (!from.isValid || !to.isValid)
            return false;
        return to > oneYearAgo;
    });
};
const toSiteData = (siteDataRaw) => {
    if (!siteDataRaw)
        return undefined;
    if (!siteDataRaw.id)
        return undefined;
    const result = emptySiteData();
    result.id = siteDataRaw.id || '';
    result.name = siteDataRaw.name || '';
    result.isPublic = siteDataRaw.isPublic ?? true;
    result.locale = toSiteLocale(siteDataRaw.locale);
    result.siteCategory = toSiteCategory(siteDataRaw.siteCategory);
    result.activities = toActivities(siteDataRaw.activities);
    result.settings = toSiteSettings(siteDataRaw.settings);
    // ADR 0016: Tillat betaling utenfor systemet moved from
    // features.noPayment.role to settings. A site that never saved the new field
    // keeps the value it had; the next save of the site writes it in its place.
    result.settings.payOutsideSystemRole = toPayOutsideSystemRole(siteDataRaw.settings?.payOutsideSystemRole ??
        siteDataRaw.features?.noPayment?.role);
    result.siteItems = (siteDataRaw.siteItems || []).map(toSpace);
    result.accessPoints = toAccessPoints(siteDataRaw.accessPoints);
    result.availability = toSlotDefs(siteDataRaw.availability);
    result.unavailability = toSlotDefs(siteDataRaw.unavailability);
    result.eventRecords = toEventRecords(siteDataRaw.eventRecords);
    result.spaceTimes = toSpaceTimeConfig(siteDataRaw.spaceTimes);
    result.pages = toPages(siteDataRaw.pages);
    result.policies = toPolicies(siteDataRaw.policies);
    result.theme = siteDataRaw.theme || defaultSiteTheme();
    result.features = toSiteFeatures(siteDataRaw.features);
    result.info = toSiteInfo(siteDataRaw.info);
    result.flags = siteDataRaw.flags || {};
    // Parsed so card numbers stored as strings read as numbers (ADR 0013, D32)
    result.punchCards = Array.isArray(siteDataRaw.punchCards)
        ? siteDataRaw.punchCards.map(toPunchCardDef)
        : [];
    result.priceOptions = toPriceOptions(siteDataRaw.priceOptions);
    result.groups = toGroupDefs(siteDataRaw.groups);
    result.version = siteDataVersion;
    result.minAppVersion = siteDataRaw.minAppVersion ?? '';
    return result;
};
const defaultSiteTheme = () => {
    return {
        logoOnDark: '',
        logoOnLight: '',
        logoHeaderSize: '40x'
    };
};
const emptySiteData = () => {
    return {
        id: '',
        name: '',
        isPublic: true,
        locale: 'no',
        siteCategory: 'dog',
        settings: emptySiteSettings(),
        pages: defaultPageSetup(),
        activities: [unspecifiedActivity()],
        spaceTimes: emptySpaceTimeConfig(),
        punchCards: [],
        priceOptions: [],
        groups: [],
        availability: [],
        unavailability: [],
        eventRecords: [],
        siteItems: [],
        accessPoints: [],
        policies: [],
        features: defaultSiteFeatures(),
        flags: {},
        theme: defaultSiteTheme(),
        info: emptySiteInfo(),
        version: siteDataVersion,
        minAppVersion: ''
    };
};
const text = Schema.String.pipe(Schema.withDecodingDefaultType(Effect.succeed('')));
// A field still read by its `to<X>` converter, in the transform below. It
// gets a schema of its own when its slice moves behind an endpoint.
const viaConverter = Schema.optional(Schema.Unknown);
/**
 * `sites/{site}` as stored, for shadow mode (authority 'legacy'). The scalar
 * fields and `policies` — the first slice written through the backend only —
 * have real schemas, with the defaults `toSiteData` applies. Every other field
 * is handed to the converter it has always had. A document without an id
 * fails to decode; `toSiteData` returns undefined for it.
 */
const StoredSiteFields = Schema.Struct({
    id: Schema.NonEmptyString,
    name: text,
    isPublic: Schema.Boolean.pipe(Schema.withDecodingDefaultType(Effect.succeed(true))),
    // 'se' is an old spelling of Swedish.
    locale: Schema.Literals(['no', 'en', 'sv', 'se']).pipe(Schema.withDecodingDefaultType(Effect.succeed('no'))),
    siteCategory: Schema.Literals(['dog', 'padel', 'other']).pipe(Schema.withDecodingDefaultType(Effect.succeed('dog'))),
    // Kept whole, extra keys (primaryColor) included, as toSiteData does.
    theme: Schema.StructWithRest(Schema.Struct({
        logoOnDark: Schema.String,
        logoOnLight: Schema.String,
        logoHeaderSize: Schema.String
    }), [Schema.Record(Schema.String, Schema.Unknown)]).pipe(Schema.withDecodingDefaultType(Effect.sync(() => ({ ...defaultSiteTheme() })))),
    flags: Schema.Record(Schema.String, Schema.Boolean).pipe(Schema.withDecodingDefaultType(Effect.sync(() => ({})))),
    minAppVersion: text,
    // Suspended: policy.ts and site.ts import each other, so PolicySchema is
    // not defined yet while this module is first evaluated.
    policies: Schema.Array(Schema.suspend(() => PolicySchema)).pipe(Schema.withDecodingDefaultType(Effect.sync(() => []))),
    activities: viaConverter,
    settings: viaConverter,
    siteItems: viaConverter,
    accessPoints: viaConverter,
    availability: viaConverter,
    unavailability: viaConverter,
    eventRecords: viaConverter,
    spaceTimes: viaConverter,
    pages: viaConverter,
    features: viaConverter,
    info: viaConverter,
    punchCards: viaConverter,
    priceOptions: viaConverter,
    groups: viaConverter,
    version: viaConverter // always the current siteDataVersion once read
});
const SiteDataType = Schema.declare((u) => typeof u === 'object' && u !== null);
const SiteDataSchema = StoredSiteFields.pipe(Schema.decodeTo(SiteDataType, SchemaTransformation.transform({
    decode: (doc) => {
        const raw = doc;
        const settings = toSiteSettings(raw.settings);
        // ADR 0016, as in toSiteData.
        settings.payOutsideSystemRole = toPayOutsideSystemRole(raw.settings?.payOutsideSystemRole ?? raw.features?.noPayment?.role);
        return {
            ...doc,
            locale: doc.locale === 'se' ? 'sv' : doc.locale,
            flags: { ...doc.flags },
            // Policy says content is always there; toPolicy passes a missing
            // one through as undefined just the same.
            policies: [...doc.policies],
            activities: toActivities(raw.activities),
            settings,
            siteItems: (raw.siteItems || []).map(toSpace),
            accessPoints: toAccessPoints(raw.accessPoints),
            availability: toSlotDefs(raw.availability),
            unavailability: toSlotDefs(raw.unavailability),
            eventRecords: toEventRecords(raw.eventRecords),
            spaceTimes: toSpaceTimeConfig(raw.spaceTimes),
            pages: toPages(raw.pages),
            features: toSiteFeatures(raw.features),
            info: toSiteInfo(raw.info),
            punchCards: Array.isArray(raw.punchCards)
                ? raw.punchCards.map(toPunchCardDef)
                : [],
            priceOptions: toPriceOptions(raw.priceOptions),
            groups: toGroupDefs(raw.groups),
            version: siteDataVersion
        };
    },
    encode: (data) => data
})));
const SiteDataStored = {
    name: 'SiteData',
    schema: SiteDataSchema,
    // Undefined for a document without an id, which the schema refuses.
    legacy: (raw) => toSiteData(raw),
    authority: 'legacy'
};
export { defaultPageSetup, defaultSiteTheme, emptyConfirmationTextConfig, emptySiteData, emptySiteInfo, emptySiteSettings, getActivityById, hasFlag, slotDescription, KnownFlags, SiteDataSchema, SiteDataStored, PageEnum, pageFromUrl, PageType, cartTicketOverQuota, spaceQuotas, spaceQuotaInfo, quotaInfoText, spacesActiveAndSorted, toSiteData };
//# sourceMappingURL=site.js.map